/**
 * hudRegions - fixed flex containers for all persistent HUD UI, replacing the
 * hand-positioned absolute offsets each surface used to carry.
 *
 * Five regions on TOKENS spacing and a shared z-plane (below the top-center
 * notification stack, far below modals):
 *
 *   top-left      - Connected Users panel
 *   top-right     - Help + Menu buttons, self-view
 *   bottom-right  - "Flying" pill, host-controls toggle (touch), mobile video
 *   bottom-center - coach marks, follow banner, host action bar (desktop)
 *   bottom-left   - level minimap (editor-created LayoutLevels only)
 *
 * The containers are pass-through (pointerEvents: none); interactive children
 * opt back in with pointerEvents: auto - same convention as getTopStack().
 */

import { TOKENS } from '../designSystem.js';

// Below TOKENS.z.notification (1500) so top-center banners always win, above
// the in-scene overlays (1000-ish legacy buttons share this plane).
const HUD_Z = String(TOKENS.z.hud);

/**
 * Flex `order` values for HUD region children. Everything defaults to 0, so a
 * region's visual stack is otherwise a function of mount order - which is lazy
 * and nondeterministic across the conditional bottom-center surfaces. Anything
 * ALWAYS-ON claims a value here so transient banners can never push it around.
 *
 *   PINNED_BOTTOM   - bottom-center: hugs the viewport edge below every
 *     transient banner (the region is `column`, so a higher order renders
 *     LOWER).
 *   MEETING_BAR     - bottom-center: the meeting toolbar claims the TRUE
 *     bottom edge, below even the host action bar (200 > PINNED_BOTTOM in a
 *     `column` region renders lower). Guests have no host bar and see just
 *     the toolbar.
 *   ABOVE_FAB_STACK - bottom-right: sits above the persistent button/pill
 *     stack (the region is `column-reverse`, so a higher order renders HIGHER).
 */
export const HUD_ORDER = { PINNED_BOTTOM: 100, MEETING_BAR: 200, ABOVE_FAB_STACK: 1 };

const REGION_STYLES = {
  'top-left': {
    top: TOKENS.space.lg,
    left: TOKENS.space.lg,
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  'top-right': {
    top: TOKENS.space.lg,
    right: TOKENS.space.lg,
    flexDirection: 'column',
    alignItems: 'flex-end',
  },
  'bottom-right': {
    bottom: TOKENS.space.lg,
    right: TOKENS.space.lg,
    // column-reverse: the first mounted child hugs the corner, later children
    // stack upward - matches how a FAB + pills naturally pile up.
    flexDirection: 'column-reverse',
    alignItems: 'flex-end',
  },
  'bottom-center': {
    bottom: '28px',
    // Inset from both edges rather than `left: 50%` + a translate. A fixed box
    // anchored only at left:50% is shrink-to-fit against the space remaining to
    // the right edge, so it silently capped every child at HALF the viewport -
    // a wide child (the host action bar, a full video-grid strip) wrapped or
    // clipped even with the whole screen free. Spanning the region and
    // centering its children costs nothing: the container is pass-through.
    left: TOKENS.space.lg,
    right: TOKENS.space.lg,
    // column: the LAST child (in order-modified order) sits lowest. Transient
    // banners all default to order 0; always-on surfaces claim a HUD_ORDER
    // value so mount timing can never reshuffle the stack.
    flexDirection: 'column',
    alignItems: 'center',
  },
  'bottom-left': {
    bottom: TOKENS.space.lg,
    left: TOKENS.space.lg,
    flexDirection: 'column-reverse',
    alignItems: 'flex-start',
  },
};

/**
 * The top-right button cluster (Help, Menu) - a row that always renders above
 * the self-view tile inside the top-right region.
 * @returns {HTMLElement}
 */
export function getTopRightButtonRow() {
  const region = getHudRegion('top-right');
  let row = document.getElementById('hud-top-right-buttons');
  if (!row) {
    row = document.createElement('div');
    row.id = 'hud-top-right-buttons';
    Object.assign(row.style, {
      display: 'flex',
      gap: TOKENS.space.sm,
      pointerEvents: 'none',
    });
    region.prepend(row);
  }
  return row;
}

/**
 * @param {'top-left'|'top-right'|'bottom-right'|'bottom-center'|'bottom-left'} name
 * @returns {HTMLElement} the (lazily created) region container
 */
export function getHudRegion(name) {
  const spec = REGION_STYLES[name];
  if (!spec) throw new Error(`Unknown HUD region: ${name}`);
  const id = `hud-${name}`;
  let region = document.getElementById(id);
  if (region) return region;

  region = document.createElement('div');
  region.id = id;
  Object.assign(region.style, {
    position: 'fixed',
    display: 'flex',
    gap: TOKENS.space.sm,
    zIndex: HUD_Z,
    pointerEvents: 'none',
    maxWidth: 'calc(100vw - 32px)',
    ...spec,
  });
  document.body.appendChild(region);
  return region;
}
